import Hotjar from "@hotjar/browser";
import * as Sentry from "@sentry/react";
import { BrowserTracing } from "@sentry/tracing";
import React, { PropsWithChildren, Suspense } from "react";
import ReactDOM from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import { QueryClient, QueryClientProvider } from "react-query";
import { Outlet, Route, RouterProvider, createBrowserRouter, createRoutesFromElements } from "react-router-dom";
import { StatsigProvider, useGate } from "statsig-react";
import { PageLoading } from "./components/PageLoading";
import {
  SENTRY_DSN,
  appServerUrl,
  auth0Config,
  getDoorsteadEnv,
  segmentWriteKey,
  statsigEnvironment,
  statsigSdkKey,
} from "./config";
import { AnalyticsProvider } from "./contexts/analytics";
import { AuthProvider, useAuth } from "./contexts/auth";
import { useIsOffline } from "./hooks/useIsOffline";
import { prefetchListingDetail } from "./hooks/useListingDetail";
import { useTenantPaymentsClosed } from "./hooks/useTenantPaymentsClosed";
import "./index.css";
import reportWebVitals from "./reportWebVitals";
import ErrorPage from "./routes/error";
import NotFoundPage from "./routes/notfound";
import { isOnline } from "./utils/isOnline";
import { lazyWithReload } from "./utils/lazyWithReload";

// Routes load on demand, so a page only downloads its own code (a public
// listing page has no reason to download payments, Stripe or rent-ready).
const LeaseNegotiationOnePage = lazyWithReload(() =>
  import("./components/LeaseNegotiationOnePage").then((module) => ({ default: module.LeaseNegotiationOnePage }))
);
const ApplicantForgotPassword = lazyWithReload(() => import("./routes/applicants/applicant-forgot-password"));
const ApplicantInvitePage = lazyWithReload(() => import("./routes/applicants/applicant-invite"));
const ApplicantLeaseStartDateRoute = lazyWithReload(() => import("./routes/applicants/applicant-lease-start-date"));
const ApplicantLoginPage = lazyWithReload(() => import("./routes/applicants/applicant-login"));
const ApplicantLoginCallbackPage = lazyWithReload(() => import("./routes/applicants/applicant-login-callback"));
const ApplicantLogoutPage = lazyWithReload(() => import("./routes/applicants/applicant-logout"));
const ApplicantLogoutCallbackPage = lazyWithReload(() => import("./routes/applicants/applicant-logout-callback"));
const ApplicantOnePage = lazyWithReload(() => import("./routes/applicants/applicant-one"));
const ApplicantSignUpPage = lazyWithReload(() => import("./routes/applicants/applicant-signup"));
const AutoPayPage = lazyWithReload(() => import("./routes/autopay"));
const FlexWalkthroughOnePage = lazyWithReload(() => import("./routes/flex-walkthrough/FlexWalkthroughOnePage"));
const ForgotPasswordPage = lazyWithReload(() => import("./routes/forgot-password"));
const HelpPage = lazyWithReload(() => import("./routes/help"));
const HomePage = lazyWithReload(() => import("./routes/home/home"));
const LeaseRenewalRoute = lazyWithReload(() => import("./routes/lease-renewal"));
const ListingDetailRoute = lazyWithReload(() => import("./routes/listing-detail"));
const ListingsPage = lazyWithReload(() => import("./routes/listings"));
const LoginPage = lazyWithReload(() => import("./routes/login"));
const LoginCallbackPage = lazyWithReload(() => import("./routes/login/callback"));
const LogoutPage = lazyWithReload(() => import("./routes/logout"));
const MaintenancePage = lazyWithReload(() => import("./routes/maintenance"));
const MakePaymentPage = lazyWithReload(() => import("./routes/make-payment"));
const OneTimePay = lazyWithReload(() => import("./routes/one-time-pay"));
const PaymentMethodsPage = lazyWithReload(() => import("./routes/payment-method"));
const PaymentsClosedPage = lazyWithReload(() => import("./routes/payments-closed"));
const RentReadyDebugPage = lazyWithReload(() => import("./routes/rent-ready/RentReadyDebugPage"));
const RentReadyOnePage = lazyWithReload(() => import("./routes/rent-ready/RentReadyOnePage"));
const ProjectRoute = lazyWithReload(() => import("./routes/rr-project/ProjectRoute"));
const SignupPage = lazyWithReload(() => import("./routes/signup"));
const TransactionHistoryPage = lazyWithReload(() => import("./routes/transaction-history"));
const VendorOnboardingPage = lazyWithReload(() => import("./routes/vendor-onboarding"));

// Shows the loading indicator while a route's code downloads.
const RouteSuspense = () => (
  <Suspense fallback={<PageLoading />}>
    <Outlet />
  </Suspense>
);

if (isOnline() && SENTRY_DSN) {
  console.log("sentry init");
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: getDoorsteadEnv(),
    integrations: [new BrowserTracing()],
    tracesSampleRate: 1.0,
  });

  const siteId = 3716052;
  const hotjarVersion = 6;

  Hotjar.init(siteId, hotjarVersion);
}

const Maintain = ({ element }: { element: React.ComponentType }) => {
  const isMaintenanceMode = useGate("tenant_portal_maintenance_mode").value;
  const Component = isMaintenanceMode ? MaintenancePage : element;
  return <Component />;
};

// Pages that take a payment, replaced by a notice once Doorstead stops
// collecting tenant payments (server-decided; see tenantPaymentsClosed).
const PaymentsGate = ({ element }: { element: React.ComponentType }) => {
  const { closed } = useTenantPaymentsClosed();
  const Component = closed ? PaymentsClosedPage : element;
  return <Component />;
};

const GatedMakePaymentPage = () => <PaymentsGate element={MakePaymentPage} />;
const GatedAutoPayPage = () => <PaymentsGate element={AutoPayPage} />;

function Routes() {
  switch (window.location.hostname) {
    case "pay.doorstead.com":
    case "mars-pay.doorstead.com":
    case "int-pay.doorstead.com":
      return (
        <Route path="/" element={<RouteSuspense />} errorElement={<ErrorPage />}>
          <Route index element={<OneTimePay />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      );
    case "vendors.doorstead.com":
    case "mars-vendors.doorstead.com":
    case "int-vendors.doorstead.com":
      return (
        <Route path="/" element={<RouteSuspense />} errorElement={<ErrorPage />}>
          <Route path="onboarding" element={<VendorOnboardingPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      );
    default:
      return (
        <Route path="/" element={<RouteSuspense />} errorElement={<ErrorPage />}>
          <Route errorElement={<ErrorPage />}>
            <Route index element={<Maintain element={HomePage} />} />
            <Route path="lsd" element={<LeaseNegotiationOnePage />} />
            <Route path="payment-method" element={<Maintain element={PaymentMethodsPage} />} />
            <Route path="payment" element={<Maintain element={GatedMakePaymentPage} />} />
            <Route path="transaction-history" element={<Maintain element={TransactionHistoryPage} />} />
            <Route path="autopay" element={<Maintain element={GatedAutoPayPage} />} />
            <Route path="application/login/callback" element={<ApplicantLoginCallbackPage />} />
            <Route path="application/logout/callback" element={<ApplicantLogoutCallbackPage />} />
            <Route path="application/logout" element={<ApplicantLogoutPage />} />
            <Route path="application/forgot-password" element={<ApplicantForgotPassword />} />
            <Route path="application/:id/signup" element={<ApplicantSignUpPage />} />
            <Route path="application/:id/login" element={<ApplicantLoginPage />} />
            <Route path="application/:id/invite" element={<ApplicantInvitePage />} />
            <Route path="application/:id/lease-start-date" element={<ApplicantLeaseStartDateRoute />} />
            <Route path="application/:id" element={<ApplicantOnePage />} />
            <Route path="rent-ready/:inspectionReportId" element={<RentReadyOnePage />} />
            <Route path="flex-walkthrough/:flexWalkthroughReportId" element={<FlexWalkthroughOnePage />} />
            <Route path="rent-ready/:inspectionReportId/debug" element={<RentReadyDebugPage />} />
            <Route path="project/:rrProjectId" element={<ProjectRoute />} />
            <Route path="tenant/:tenantId/renewal/:renewalId" element={<Maintain element={LeaseRenewalRoute} />} />
            <Route path="one-time-pay" element={<OneTimePay />} />
            <Route path="vendor-onboarding" element={<VendorOnboardingPage />} />
            <Route path="signup" element={<SignupPage />} />
            <Route path="logout" element={<LogoutPage />} />
            <Route path="login" element={<LoginPage />}>
              <Route path="callback" element={<LoginCallbackPage />} />
            </Route>
            <Route path="forgot-password" element={<ForgotPasswordPage />} />
            <Route path="help" element={<HelpPage />} />
            <Route path="listings" element={<ListingsPage />} />
            <Route path="listings/:listingId" element={<ListingDetailRoute />} />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      );
  }
}

const router = createBrowserRouter(createRoutesFromElements(Routes()));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: false,
    },
  },
});

// A listing page's data and code start downloading now, side by side, rather
// than the data waiting for the code to arrive and render first.
const listingPageMatch = window.location.pathname.match(/^\/listings\/([^/]+)/);
if (listingPageMatch) {
  void prefetchListingDetail(queryClient, decodeURIComponent(listingPageMatch[1]));
  void import("./routes/listing-detail");
}

// Public listing pages need neither the signed-in user nor a feature gate, so
// they render straight away instead of waiting on the Auth0 session check and
// Statsig. The user still reaches Statsig once the session check finishes.
// Decided by the page the visitor landed on, like TenantPortalProvider.
const startsOnListings = window.location.pathname.startsWith("/listings");

export const StatsigSetup = ({
  children,
  userID,
  email,
  customIDs,
}: PropsWithChildren & {
  userID?: string;
  email?: string;
  customIDs?: Record<string, string>;
}) => {
  const isOffline = useIsOffline();
  const { user, loading } = useAuth();
  if (isOffline) {
    return <>{children}</>;
  }
  if (loading && !startsOnListings) {
    return <PageLoading />;
  }

  return (
    <StatsigProvider
      sdkKey={statsigSdkKey}
      user={{
        userID: userID || user?.doorsteadUserId,
        email: email || user?.email,
        customIDs,
      }}
      waitForInitialization={!startsOnListings}
      initializingComponent={<PageLoading />}
      options={{
        environment: { tier: statsigEnvironment() },
      }}
    >
      {children}
    </StatsigProvider>
  );
};

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);
root.render(
  <React.StrictMode>
    <AnalyticsProvider segmentWriteKey={segmentWriteKey()}>
      <AuthProvider auth0Config={auth0Config()} baseUrl={appServerUrl()}>
        <StatsigSetup>
          <QueryClientProvider client={queryClient}>
            <HelmetProvider>
              <RouterProvider router={router} />
            </HelmetProvider>
          </QueryClientProvider>
        </StatsigSetup>
      </AuthProvider>
    </AnalyticsProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// Unregister service worker and reload
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.ready.then((registration) => {
    registration.unregister().then(() => {
      window.location.reload();
    });
  });
}
